import React, { useState, useRef, useCallback } from 'react'
import * as d3 from 'd3'
import { ExtendedFeatureCollection, ExtendedFeature } from 'd3'
import Scrollchor from 'react-scrollchor'
import './styles.scss'

const isMobile = window.innerWidth < 800
const width = 1000
const height = isMobile ? 1000 : 700

interface Props {
  filteredCommunes: ExtendedFeatureCollection
  data: []
}

export const InteractiveMap = ({ filteredCommunes, data }: Props) => {
  const [popupPosition, setPopupPosition] = useState([0, 0, 0, 0])
  const [communeData, setCommuneData] = useState(null)
  const mapContainer = useRef(null)

  const selectedMunicipalityData = communeData
    ? data.find(o => o.ofsnr === communeData.GMDNR).values
    : [0]

  const getxPosition = x => {
    let p = 0
    if (x < 200) {
      p = 210
    } else if (x > window.innerWidth - 200) {
      p = window.innerWidth - 210
    } else {
      p = x
    }
    return p
  }

  const handleChangeItem = useCallback((properties, position) => {
    const parentPosition = mapContainer.current.getBoundingClientRect()
    if (properties) {
      setPopupPosition([
        getxPosition(position.x),
        position.y < 0 ? 0 : position.y,
        position.x > parentPosition.x + parentPosition.width / 2,
        position.y > parentPosition.y + parentPosition.height / 2
      ])
      setCommuneData(properties)
    } else {
      setCommuneData(null)
    }
  }, [])

  return (
    <div ref={mapContainer}>
      {communeData && (
        <div
          className={`popup ui raised compact segment ${
            popupPosition[2] ? 'pleft' : ''
          } ${popupPosition[3] ? 'ptop' : ''}`}
          style={{
            transform: `translate(${popupPosition[0]}px, ${popupPosition[1]}px)`
          }}
        >
          <div>
            <b>{communeData.GMDNAME}</b>
          </div>
          <div className='content'>
            {selectedMunicipalityData.length > 1 ? (
              <div>
                <div>
                  <b>{selectedMunicipalityData[0]}</b> ans
                </div>
                <div className='light'>
                  {selectedMunicipalityData[1]} petite
                  {selectedMunicipalityData[1] > 1 ? 's' : ''} entreprise
                  {selectedMunicipalityData[1] > 1 ? 's' : ''}
                </div>
              </div>
            ) : (
              <div>
                <div>Aucune petite entreprise de ce type</div>
                <div className='light'>
                  Lisez la méthode pour en savoir plus.
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      <MapBackground
        data={data}
        hoverHandler={handleChangeItem}
        communes={filteredCommunes}
      />
    </div>
  )
}

interface MapProps {
  data: []
  communes: ExtendedFeatureCollection
  hoverHandler: () => void
}

const MapBackground = React.memo(
  ({ data, communes, hoverHandler }: MapProps) => {
    const minValue = d3.min(data, c => c.values[0])
    const maxValue = d3.max(data, c => c.values[0])

    const color = d3
      .scaleLinear()
      .domain([minValue, maxValue])
      .range([d3.rgb('#ea75be11'), d3.rgb('#ea75beff')])
      .interpolate(d3.interpolateRgb.gamma(2.2))

    const projection = d3.geoMercator().fitSize([width, height], communes)
    const path = d3.geoPath().projection(projection)

    const handleMouseEnter = function(e: SVGPathElement, properties: {}) {
      setTimeout(() => {
        d3.select(e).classed('active', true)
      }, 120)

      hoverHandler(properties, e.getBoundingClientRect())
    }

    const handleMouseLeave = function(e: SVGPathElement) {
      setTimeout(() => {
        d3.select(e).classed('active', false)
      }, 120)

      hoverHandler(null, null)
    }

    console.log('Draw map')

    return (
      <div className='map-background-container'>
        <svg className='mapSwitzerland' viewBox={'0 0 ' + width + ' ' + height}>
          <pattern
            id='diagonalHatch'
            patternUnits='userSpaceOnUse'
            width='4'
            height='4'
          >
            <path
              d='M-1,1 l2,-2
           M0,4 l4,-4
           M3,5 l2,-2'
              style={{ stroke: '#00000066', strokeWidth: 1 }}
            />
          </pattern>
          <g className='topology'>
            {communes.features.map((feature: ExtendedFeature, i: number) => {
              const municipalityData = data.find(
                o => o.ofsnr === feature.properties.GMDNR
              )
              return (
                <path
                  key={i}
                  d={path(feature)}
                  fill={
                    municipalityData
                      ? municipalityData.values.length > 1
                        ? color(municipalityData.values[0])
                        : 'url(#diagonalHatch)'
                      : '#ffffff'
                  }
                  onMouseEnter={e => {
                    handleMouseEnter(e.currentTarget, feature.properties)
                  }}
                  onMouseLeave={e => {
                    handleMouseLeave(e.currentTarget)
                  }}
                />
              )
            })}
          </g>
        </svg>
        <div className='legend'>
          <div className='legend-inner'>
            <div style={{ marginBottom: '0.5rem' }}>
              Durée de vie médiane des petites entreprises
            </div>
            <div className='colorscale'>
              <div
                style={{
                  backgroundColor: color(minValue)
                }}
              />
              <div
                style={{
                  backgroundColor: color(minValue + (maxValue - minValue) / 5)
                }}
              />
              <div
                style={{
                  backgroundColor: color(
                    minValue + ((maxValue - minValue) / 5) * 2
                  )
                }}
              />
              <div
                style={{
                  backgroundColor: color(
                    minValue + ((maxValue - minValue) / 5) * 3
                  )
                }}
              />
              <div
                style={{
                  backgroundColor: color(
                    minValue + ((maxValue - minValue) / 5) * 4
                  )
                }}
              />
              <div
                style={{
                  backgroundColor: color(maxValue)
                }}
              />
            </div>
            <div className='legend-label'>
              <div>Moins d'un an</div>
              <div>{Math.round(maxValue)} ans</div>
            </div>
          </div>
        </div>
      </div>
    )
  }
)
